import type { BrandAlertCreativeData, BrandAlertsConfig, BrandAlertSurface, TrackPayload, Transport } from './types';

/** Builds the request headers for a platform. Web sends the CSRF token, iOS a
 * bearer token — the only thing that differed between the two old subclasses'
 * `getAjaxHeaders()`. */
export type AuthHeaders = (config: BrandAlertsConfig) => Record<string, string>;

export const webAuthHeaders: AuthHeaders = (config) => ({
    'X-CSRFToken': window.csrftoken?.() || config.csrftoken || '',
    Accept: 'application/json',
});

export const iosAuthHeaders: AuthHeaders = (config) => ({
    Authorization: config.accessToken ?? '',
    Accept: 'application/json',
});

/**
 * The only thing that talks to the brand alert endpoints.
 */
export class HttpTransport implements Transport {
    constructor(
        private readonly _config: BrandAlertsConfig,
        private readonly _authHeaders: AuthHeaders,
        /** Required by the serving endpoint, which 400s without it. Sent on both
         * fetches — the surface does not change between a load and a rotation. */
        private readonly _surface: BrandAlertSurface,
        /**
         * Which `Viewed` the display is recorded against, resolved per request.
         *
         * The video player page and iOS use the page's uuid, known upfront. BVA
         * overrides this with the chat session's own `Viewed`, which does not
         * exist until the HCP's first question and changes on a conversation
         * reset — so it has to be read at fetch time, not captured here.
         */
        private readonly _resolveViewedUuid: () => string | null = () => _config.viewedUuid,
    ) {}

    async fetchCreative(): Promise<BrandAlertCreativeData> {
        const data = this._baseParams();
        if (this._config.forceCreativeId) {
            data.force_brand_alert_creative_id = this._config.forceCreativeId;
        }
        return this._fetch(data);
    }

    async fetchNextCreative(): Promise<BrandAlertCreativeData> {
        // No forced creative id on purpose — see the interface docs.
        return this._fetch(this._baseParams());
    }

    private _baseParams(): Record<string, string> {
        const viewedUuid = this._resolveViewedUuid();
        if (!viewedUuid) {
            throw new Error(`brand-alerts: no viewed uuid to serve ${this._surface} against`);
        }
        return { viewed_uuid: viewedUuid, surface: this._surface };
    }

    track(payload: TrackPayload): void {
        $.ajax({
            url: this._config.trackingUrl,
            dataType: 'json',
            contentType: 'application/json; charset=utf-8',
            method: 'POST',
            data: JSON.stringify(payload),
            headers: this._authHeaders(this._config),
        });
    }

    private _fetch(data: Record<string, string>): Promise<BrandAlertCreativeData> {
        // jQuery's promise is not a native one; wrap so callers can await. A 404
        // (nothing to serve) rejects, matching the original `.fail` path.
        return new Promise((resolve, reject) => {
            (
                $.ajax({
                    url: this._config.creativeUrl,
                    headers: this._authHeaders(this._config),
                    data,
                }) as JQuery.jqXHR<BrandAlertCreativeData>
            )
                .done((creative) => resolve(creative))
                .fail(() => reject(new Error('brand-alerts: no creative to serve')));
        });
    }
}
